import { useTranslation } from 'react-i18next';
import {
  formatDateShort,
  formatDateWithLocaleDay,
} from '../../../../../utils/dateUtils';

// The server ships widget items with both machine fields (status code, ISO
// date) and prebuilt English labels (statusLabel, dateLabel). The English
// labels exist as fallbacks only — rendering goes through these helpers so a
// German question gets a German card (FLA-8201).

// Keyed by the server's LABEL, not its status code: the code is a styling
// bucket that deliberately collapses distinctions the label keeps —
// REIMBURSED maps to status "approved" with label "Reimbursed", DECLINED to
// "rejected"/"Declined" — so translating from the code would rename
// a reimbursed expense to "Approved" (caught by review on this PR).
const KNOWN_STATUS_LABELS = new Set([
  'approved',
  'pending',
  'rejected',
  'declined',
  'draft',
  'reimbursed',
  'open',
  'closed',
  'cancelled',
  'unknown',
]);

export function useStatusLabel(): (label: string) => string {
  const { t } = useTranslation();
  return (label) => {
    const key = label.trim().toLowerCase();
    return KNOWN_STATUS_LABELS.has(key)
      ? t(`aiChat.widgets.status.${key}`)
      : label;
  };
}

export function widgetDate(
  isoDate: string | undefined,
  fallback: string,
): string {
  if (!isoDate) return fallback;
  try {
    return formatDateShort(isoDate);
  } catch {
    return fallback;
  }
}

export function widgetDateRange(
  startDate: string | undefined,
  endDate: string | undefined,
  fallback: string,
): string {
  if (!startDate) return fallback;
  const start = widgetDate(startDate, fallback);
  if (!endDate || endDate === startDate) return start;
  return `${start} – ${widgetDate(endDate, fallback)}`;
}

export function widgetDayLabel(
  isoDate: string | undefined,
  fallback: string,
): string {
  if (!isoDate) return fallback;
  try {
    // The hub's established weekday + date format (review catch: don't
    // reimplement it with a divergent comma variant).
    return formatDateWithLocaleDay(isoDate);
  } catch {
    return fallback;
  }
}
